import {CanEditReturnValue} from 'analysis-tool/EditAnalysisHelpers';
import {trackEvent} from 'analytics/Analytics';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisFlowchartAction} from './AnalysisFlowchartActionTypes';
import {AnalysisFlowchartAvailableAction} from './AnalysisFlowchartActionTypes';
import {
  INTERNAL_FLOWCHART_ACTION_FUNCTIONS,
  INTERNAL_FLOWCHART_ACTION_SKIP_REFRESH,
} from './internal-action-functions/ActionFunctions';

/**
 * The unique parameters for an action's internal function, following the shared `pipeline`.
 *
 * `doAction` is called with these parameters.
 *
 * @package
 */
export type ActionParameters<T extends AnalysisFlowchartAction> =
  Parameters<(typeof INTERNAL_FLOWCHART_ACTION_FUNCTIONS)[T]> extends [
    Pick<PipelineGraphQL, 'id' | 'steps'>,
    ...infer Rest,
  ]
    ? Rest
    : never;

/**
 * The interface passed to step-level components, through `AnalysisFlowchartContext`, to run actions.
 *
 * Low-level components use `getActionInfo` to check if an action is available for a particular step and, if
 * so, to run the action.
 *
 * The actual logic for whether an action is enabled, or how to run the action, is abstracted.
 *
 * This pattern simplifies unit testing, because low-level components need only to stub out if an action is or
 * is not available, without creating full pipeline fixtures (which can get really complicated!).
 *
 * Example:
 *
 *      const {enabled, message, doAction} = getActionInfo(AnalysisFlowchartAction.DELETE_STEP, stepId);
 *      if (enabled)
 *          doAction(stepId);
 *      else
 *          pushToast(message);
 */
export type GetActionInfoFn<T extends AnalysisFlowchartAction = AnalysisFlowchartAction> = (
  action: T,
  ...stepIdsToModify: Array<string>
) =>
  | {enabled: true; message: null; doAction: (...args: ActionParameters<T>) => Promise<void>}
  | {enabled: false; message: string | null; doAction: null};

/**
 * Translate the list of available actions (passed by the parent of <AnalysisFlowchart>) to the interface
 * used by low-level components (see `GetActionInfoFn`).
 *
 * Parents of <AnalysisFlowchart> registers available actions, e.g.:
 *
 *       availableActions: [{action: AnalysisFlowchartAction.REPLACE_STEP}]
 *
 * Step-level components access available actions, e.g.:
 *
 *       const {enabled, message, doAction} = getActionInfo(AnalysisFlowchartAction.REPLACE_STEP, stepId);
 *       if (enabled)
 *           doAction(stepId, newStepType);
 *       else
 *           pushToast(message);
 *
 * `doAction` will first run the internal action logic (`ActionFunctions.ts`), followed `refresh` and the
 * optional `onAction` callback.
 */
export function createGetActionInfoFn({
  pipeline,
  availableActions,
  canModifyStep,
  canModifyAnalysis,
  refresh,
}: {
  pipeline: Pick<PipelineGraphQL, 'id' | 'steps'>;
  availableActions?: Array<AnalysisFlowchartAvailableAction<AnalysisFlowchartAction>>;
  canModifyStep?: (stepId: string) => CanEditReturnValue;
  canModifyAnalysis?: () => CanEditReturnValue;
  refresh?: () => Promise<unknown>;
}): GetActionInfoFn {
  function getActionInfo<T extends AnalysisFlowchartAction>(
    action: T,
    ...stepIdsToModify: Array<string>
  ): ReturnType<GetActionInfoFn<T>> {
    const availableAction = (availableActions ?? []).find(
      (availableAction) => availableAction.action === action
    );

    // Check if the action should be hidden because it is not available in general
    if (availableAction == null) {
      return {enabled: false, message: null, doAction: null};
    }

    // Check if the action should be disabled for this specific step
    if (stepIdsToModify.length > 0 && availableAction.getDisabledMessage != null) {
      const disabledMessage = availableAction.getDisabledMessage(stepIdsToModify[0]);
      if (disabledMessage != null) {
        return {enabled: false, message: disabledMessage, doAction: null};
      }
    }

    // Check if the action should be disabled because a particular step cannot be modified.
    // (In the future, we may want to distinguish read-only actions from those that modify the pipeline.)
    if (canModifyStep != null) {
      const disabledMessages = stepIdsToModify
        .map((stepId) => canModifyStep(stepId))
        .filter(({canEdit}) => !canEdit)
        .map(({message}) => message);

      if (disabledMessages.length > 0) {
        return {enabled: false, message: disabledMessages[0], doAction: null};
      }
    }

    if (canModifyAnalysis != null) {
      const {canEdit, message} = canModifyAnalysis();
      if (!canEdit) {
        return {enabled: false, message, doAction: null};
      }
    }

    const skipRefresh = INTERNAL_FLOWCHART_ACTION_SKIP_REFRESH[action];

    const doAction = async (...args: ActionParameters<T>): Promise<void> => {
      // @ts-expect-error TS2556: A spread argument must either have a tuple type or be passed to a rest parameter
      const returnValue = await INTERNAL_FLOWCHART_ACTION_FUNCTIONS[action](pipeline, ...args);
      // In the future, refreshing may not be necessary for read-only actions.
      if (refresh && !skipRefresh) {
        await refresh();
      }
      availableAction.onAction?.(returnValue);

      trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.CLICKED_FLOWCHART_ACTION, {
        pipeline_id: pipeline.id,
        flowchart_action: action,
      });
    };

    return {
      enabled: true,
      message: null,
      doAction: skipRefresh ? doAction : (...args) => withLoadingScreen(doAction(...args)),
    };
  }

  return getActionInfo;
}
